import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Info, FileJs } from '@phosphor-icons/react';
import { CorrectionModal } from '../components/evv-resolution/CorrectionModal';
import { EVVSchedulerCard } from '../components/evv-resolution/EVVSchedulerCard';
import { EVVSummaryCards } from '../components/evv-resolution/EVVSummaryCards';
import { EVVResolutionFilters } from '../components/evv-resolution/EVVResolutionFilters';
import { EVVResolutionTable } from '../components/evv-resolution/EVVResolutionTable';
import { EVVApprovalDialog, EVVSchedulerHistoryDialog } from '../components/evv-resolution/EVVApprovalDialog';
import { EVVCommonIssuesPanel, EVVBulkActionBar } from '../components/evv-resolution/EVVPanelsAndBars';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { MockedEvvBanner } from '../components/SandataModeBadge';
import { logger } from '../lib/logger';

const EVVResolutionPage = () => {
  const { user } = useAuth();
  const canApprove = user?.permissions?.includes('evv.approve') ||
    user?.role === 'Admin' || user?.azai_role === 'admin';

  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [commonIssues, setCommonIssues] = useState([]);
  const [showIssuesPanel, setShowIssuesPanel] = useState(false);

  // Approval modal state
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState('');
  const [approvalEntryId, setApprovalEntryId] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvalProcessing, setApprovalProcessing] = useState(false);

  // Scheduler state
  const [schedulerEnabled, setSchedulerEnabled] = useState(false);
  const [schedulerLastRun, setSchedulerLastRun] = useState(null);
  const [schedulerHistory, setSchedulerHistory] = useState([]);
  const [schedulerToggling, setSchedulerToggling] = useState(false);
  const [triggerRunning, setTriggerRunning] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const [editForm, setEditForm] = useState({});

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/evv-resolution/summary');
      setSummary(res.data);
    } catch (e) { logger.error('Failed to load summary', e); }
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 50, sort_by: sortBy, sort_order: sortOrder };
      if (filterStatus !== 'all') params.status = filterStatus;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/evv-resolution/entries', { params });
      setEntries(res.data.entries || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load entries');
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, searchTerm, dateFrom, dateTo, sortBy, sortOrder]);

  const fetchSchedulerStatus = useCallback(async () => {
    try {
      const res = await api.get('/evv-resolution/scheduler/status');
      setSchedulerEnabled(res.data.enabled);
      setSchedulerLastRun(res.data.last_run);
    } catch (e) { logger.error('Failed to load scheduler status', e); }
  }, []);

  const fetchSchedulerHistory = async () => {
    try {
      const res = await api.get('/evv-resolution/scheduler/history', { params: { limit: 10 } });
      setSchedulerHistory(res.data.runs || []);
    } catch (e) { logger.error('Failed to load scheduler history', e); }
  };

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/evv-resolution/config');
      setCommonIssues(res.data.common_issues || []);
    } catch (e) { logger.error('Failed to load config', e); }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchSchedulerStatus();
    fetchConfig();
  }, [fetchSummary, fetchSchedulerStatus, fetchConfig]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleToggleScheduler = async (enabled) => {
    setSchedulerToggling(true);
    try {
      await api.put('/evv-resolution/scheduler/toggle', { enabled });
      setSchedulerEnabled(enabled);
      toast.success(enabled ? 'Auto-submit enabled' : 'Auto-submit disabled');
    } catch {
      toast.error('Failed to toggle scheduler');
    } finally {
      setSchedulerToggling(false);
    }
  };

  const handleTriggerNow = async () => {
    setTriggerRunning(true);
    try {
      const res = await api.post('/evv-resolution/scheduler/trigger');
      toast.success(`Auto-submit triggered: ${res.data.accepted || 0} accepted, ${res.data.failed || 0} failed`);
      fetchSummary();
      fetchEntries();
      fetchSchedulerStatus();
    } catch {
      toast.error('Failed to trigger auto-submit');
    } finally {
      setTriggerRunning(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    setSelectedIds(selectedIds.length === entries.length ? [] : entries.map((e) => e.id));
  };

  const handleRequestApproval = async (entryIds) => {
    try {
      const res = await api.post('/evv-resolution/entries/bulk-request-approval', { entry_ids: entryIds });
      toast.success(`${res.data.updated} entries sent for supervisor approval`);
      setSelectedIds([]);
      fetchEntries();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request approval');
    }
  };

  const openApprovalModal = (action, entryId = null) => {
    setApprovalAction(action);
    setApprovalEntryId(entryId);
    setApprovalNotes('');
    setApprovalModalOpen(true);
  };

  const handleApprovalConfirm = async () => {
    setApprovalProcessing(true);
    try {
      const ids = approvalEntryId ? [approvalEntryId] : selectedIds;
      if (ids.length === 0) {
        toast.error('No entries selected');
        return;
      }
      const res = await api.post('/evv-resolution/entries/bulk-approval', {
        entry_ids: ids,
        action: approvalAction,
        notes: approvalNotes || null,
      });
      toast.success(`${res.data.processed} entries ${approvalAction === 'approve' ? 'approved' : 'rejected'}`);
      setApprovalModalOpen(false);
      setSelectedIds([]);
      fetchEntries();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${approvalAction}`);
    } finally {
      setApprovalProcessing(false);
    }
  };

  const handleSubmitSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select entries to submit');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/evv-resolution/entries/submit', { entry_ids: selectedIds });
      const d = res.data;
      if (d.failed > 0) {
        toast.warning(`${d.accepted} accepted, ${d.failed} failed. Check error column.`);
      } else {
        toast.success(`${d.accepted} entries accepted by Sandata`);
      }
      setSelectedIds([]);
      fetchEntries();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitSingle = async (entryId) => {
    setSelectedIds([entryId]);
    // submit on next tick so state propagates
    setTimeout(() => handleSubmitSelected(), 0);
  };

  const handleExportJSON = async () => {
    const ids = selectedIds.length > 0
      ? selectedIds
      : entries.filter((e) => e.sandata_status === 'failed').map((e) => e.id);
    if (ids.length === 0) {
      toast.error('No entries to export.');
      return;
    }
    try {
      const res = await api.post('/evv-resolution/entries/export-json', { entry_ids: ids });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `azai_evv_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('JSON exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const openEditModal = (entry) => {
    setEditEntry(entry);
    setEditForm({
      patient_first_name: entry.patient_name?.split(' ')[0] || '',
      patient_last_name: entry.patient_name?.split(' ').slice(1).join(' ') || '',
      patient_medicaid_id: entry.patient_medicaid_id || '',
      payer_code: entry.payer_code || '',
      worker_first_name: entry.employee_name?.split(' ')[0] || '',
      worker_last_name: entry.employee_name?.split(' ').slice(1).join(' ') || '',
      time_in: entry.time_in || '',
      time_out: entry.time_out || '',
      service_date: entry.service_date || '',
      service_code: entry.service_code || '',
      verification_method: entry.verification_method || 'GPS',
      manual_reason_code: entry.manual_reason_code || '',
      resolution_notes: '',
    });
    setEditModalOpen(true);
  };

  const handleSaveCorrection = async () => {
    if (!editEntry) return;
    setSaving(true);
    try {
      const correction = {};
      Object.entries(editForm).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) correction[key] = value;
      });
      const res = await api.put(`/evv-resolution/entries/${editEntry.id}`, correction);
      if (res.data.success) {
        toast.success(`Correction saved & sent for approval (${res.data.changes_applied.length} fields updated)`);
        setEditModalOpen(false);
        fetchEntries();
        fetchSummary();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save correction');
    } finally {
      setSaving(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm(''); setDateFrom(''); setDateTo(''); setFilterStatus('all');
    setSortBy('updated_at'); setSortOrder('desc'); setPage(1);
  };

  const handleStatusFilter = (newStatus) => { setFilterStatus(newStatus); setPage(1); };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="evv-resolution-page">
      <MockedEvvBanner context="evv-resolution" />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            EVV Resolution Center
          </h1>
          <p className="text-slate-500 mt-1">
            Review, correct, approve, and submit EVV entries to Sandata
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowIssuesPanel(!showIssuesPanel)} data-testid="toggle-issues-panel">
            <Info size={16} className="mr-2" /> Common Issues
          </Button>
          <Button variant="outline" onClick={handleExportJSON} data-testid="export-json-btn">
            <FileJs size={16} className="mr-2" /> Export JSON
          </Button>
        </div>
      </div>

      <EVVBulkActionBar
        selectedIds={selectedIds}
        entries={entries}
        canApprove={canApprove}
        submitting={submitting}
        onRequestApproval={handleRequestApproval}
        onOpenApproval={openApprovalModal}
        onSubmitSelected={handleSubmitSelected}
      />

      <EVVSchedulerCard
        enabled={schedulerEnabled}
        lastRun={schedulerLastRun}
        toggling={schedulerToggling}
        triggerRunning={triggerRunning}
        onToggle={handleToggleScheduler}
        onTriggerNow={handleTriggerNow}
        onShowHistory={() => { fetchSchedulerHistory(); setShowHistoryModal(true); }}
      />

      {showIssuesPanel && <EVVCommonIssuesPanel issues={commonIssues} />}

      <EVVSummaryCards summary={summary} filterStatus={filterStatus} onFilterChange={handleStatusFilter} />

      <EVVResolutionFilters
        searchTerm={searchTerm}
        setSearchTerm={(v) => { setSearchTerm(v); setPage(1); }}
        filterStatus={filterStatus}
        setFilterStatus={handleStatusFilter}
        dateFrom={dateFrom}
        setDateFrom={(v) => { setDateFrom(v); setPage(1); }}
        dateTo={dateTo}
        setDateTo={(v) => { setDateTo(v); setPage(1); }}
        sortBy={sortBy}
        setSortBy={(v) => { setSortBy(v); setPage(1); }}
        sortOrder={sortOrder}
        toggleSortOrder={() => setSortOrder((o) => o === 'asc' ? 'desc' : 'asc')}
        onClearFilters={handleClearFilters}
      />

      <EVVResolutionTable
        entries={entries}
        loading={loading}
        total={total}
        page={page}
        totalPages={totalPages}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        onEditEntry={openEditModal}
        onRequestApproval={handleRequestApproval}
        onOpenApproval={openApprovalModal}
        onSubmitSingle={handleSubmitSingle}
        onPageChange={setPage}
        canApprove={canApprove}
      />

      <CorrectionModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        editEntry={editEntry}
        editForm={editForm}
        setEditForm={setEditForm}
        onSave={handleSaveCorrection}
        saving={saving}
      />

      <EVVApprovalDialog
        open={approvalModalOpen}
        onOpenChange={setApprovalModalOpen}
        action={approvalAction}
        entryId={approvalEntryId}
        selectedIds={selectedIds}
        notes={approvalNotes}
        setNotes={setApprovalNotes}
        processing={approvalProcessing}
        onConfirm={handleApprovalConfirm}
      />

      <EVVSchedulerHistoryDialog
        open={showHistoryModal}
        onOpenChange={setShowHistoryModal}
        history={schedulerHistory}
      />
    </div>
  );
};

export default EVVResolutionPage;
